import { useEffect, useRef } from 'react';
import { KeyHint } from '@/lib';
import { GLOBAL_SHORTCUTS } from './useKeyboardMap';

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Modal sheet listing every global keyboard shortcut. Triggered by `?`
 * via the centralized keyboard map. Renders the same `KeyHint`
 * primitive screens use inline so the visual presentation of keys is
 * consistent.
 */
export function ShortcutSheet({ open, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    // Move focus into the dialog so Esc-to-close works without an extra click.
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-30 flex items-center justify-center bg-gray-900/30 p-sp-9"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-r-card border border-gray-300 bg-gray-50 p-sp-6 shadow-lg focus:outline-none"
      >
        <header className="mb-sp-4 flex items-baseline justify-between">
          <h2 className="text-fs-statement font-medium text-gray-900">
            Keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-fs-meta text-info hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          >
            Close
          </button>
        </header>
        <dl className="space-y-sp-2">
          {GLOBAL_SHORTCUTS.map((entry) => (
            <div
              key={entry.keys.join('+')}
              className="flex items-center justify-between"
            >
              <dt className="text-fs-body text-gray-900">{entry.description}</dt>
              <dd>
                <KeyHint keys={entry.keys} />
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-sp-4 text-fs-meta text-gray-700">
          Shortcuts that target a specific entity (acknowledge an alert,
          analyze an asset) act on the currently focused item.
        </p>
      </div>
    </div>
  );
}
